import { scaleValue } from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type PredictiveSearch } from '@/scripts/optional/predictive-search'
import { type DetailsModal } from '@/scripts/theme/details-modal'
import { type HeaderMenu } from '@/scripts/theme/header-menu'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { SELECTORS } from '@/scripts/core/global'

let mobileMenuScrollTop: number | undefined

export function lockHeaderDrawerScroll() {
	let mobileViewport = window.innerWidth < 990
	document.addEventListener(
		'click',
		(event) => {
			const target = event.target
			if (!(target instanceof Element) || window.innerWidth >= 990) return
			const summary = target.closest('header-drawer > details > summary')
			if (!summary || summary.parentElement?.hasAttribute('open')) return
			mobileMenuScrollTop = window.scrollY
		},
		{ capture: true }
	)
	window.addEventListener(
		'scroll',
		() => {
			if (
				window.innerWidth >= 990 ||
				mobileMenuScrollTop === undefined ||
				!document.querySelector('header-drawer > details[open]')
			) {
				mobileMenuScrollTop = undefined
				return
			}
			if (window.scrollY !== mobileMenuScrollTop)
				window.scrollTo(0, mobileMenuScrollTop)
		},
		{ passive: true }
	)
	window.addEventListener('resize', () => {
		const isMobile = window.innerWidth < 990
		if (!isMobile) mobileMenuScrollTop = undefined
		else if (
			!mobileViewport &&
			document.querySelector('header-drawer > details[open]')
		)
			mobileMenuScrollTop = window.scrollY
		mobileViewport = isMobile
	})
}

export class StickyHeader extends UcoastEl {
	static htmlSelector = 'sticky-header'
	preventHide: boolean = false
	preventReveal: boolean = false
	header: HTMLElement
	headerIsAlwaysSticky?: boolean
	headerBounds: DOMRect | {} = {}
	currentScrollTop: number = 0
	predictiveSearch?: PredictiveSearch
	onScrollHandler!: (event: Event) => void
	hideHeaderOnScrollUp!: () => void
	searchModal?: DetailsModal
	isScrolling?: number
	topTransitionTimeout?: number
	scrollDirectionAnchor: number = 0
	disclosures?: [] | NodeListOf<HeaderMenu>
	setHeaderHeightHandler?: () => void
	headerResizeObserver?: ResizeObserver
	headerMediaQuery?: MediaQueryList
	accountComponent?: HTMLElement
	backdrop?: HTMLElement
	usesTransparentBackdrop: boolean = false
	setAccountDialogPositionHandler?: () => void
	constructor() {
		super()
		this.header = q.rs(SELECTORS.sectionHeader)
	}

	override connectedCallback() {
		this.header = q.rs(SELECTORS.sectionHeader)
		this.backdrop = q.os<HTMLElement>('[data-header-scroll-backdrop]')
		this.usesTransparentBackdrop =
			this.hasAttribute('data-homepage-transparent-overlap') &&
			this.getAttribute('data-header-background-type') === 'transparent'
		this.setBackdropState('top')
		this.headerIsAlwaysSticky =
			this.getAttribute('data-sticky-type') === 'always' ||
			this.getAttribute('data-sticky-type') === 'reduce-logo-size'
		this.headerBounds = {}

		this.setHeaderHeight()

		const setHeaderHeightHandler = this.setHeaderHeight.bind(this)
		this.setHeaderHeightHandler = setHeaderHeightHandler
		this.headerMediaQuery = window.matchMedia('(max-width: 990px)')
		this.headerMediaQuery.addEventListener(
			'change',
			setHeaderHeightHandler
		)

		this.headerResizeObserver = new ResizeObserver(() => {
			this.setHeaderHeight()
		})
		this.headerResizeObserver.observe(this.header)
		this.accountComponent = q.os<HTMLElement>('shopify-account', this)
		const setAccountDialogPositionHandler =
			this.setAccountDialogPosition.bind(this)
		this.setAccountDialogPositionHandler = setAccountDialogPositionHandler
		this.accountComponent?.addEventListener(
			'click',
			setAccountDialogPositionHandler,
			{ capture: true }
		)

		if (this.headerIsAlwaysSticky && this.header) {
			this.header.classList.add('shopify-section-header-sticky')
		}

		this.currentScrollTop = 0
		this.scrollDirectionAnchor = 0
		this.preventReveal = false
		this.predictiveSearch = q.os<PredictiveSearch>(
			'predictive-search',
			this
		)

		this.onScrollHandler = this.onScroll.bind(this)
		this.hideHeaderOnScrollUp = () => (this.preventReveal = true)

		this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
		window.addEventListener('scroll', this.onScrollHandler, false)

		this.createObserver()
	}

	setHeaderHeight() {
		if (!this.header) throw new Error('no header element found')
		const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile'
		const headerHeight = `calc(${scaleValue(this.header.offsetHeight, viewport)} * var(--ax))`
		document.documentElement.style.setProperty(
			'--header-height',
			headerHeight
		)
		if (this.hasAttribute('data-homepage-overlap-enabled')) {
			document.documentElement.style.setProperty(
				'--homepage-header-overlap-active',
				headerHeight
			)
		}
	}

	override disconnectedCallback() {
		if (this.headerMediaQuery && this.setHeaderHeightHandler) {
			this.headerMediaQuery.removeEventListener(
				'change',
				this.setHeaderHeightHandler
			)
		}
		this.headerResizeObserver?.disconnect()
		if (this.accountComponent && this.setAccountDialogPositionHandler) {
			this.accountComponent.removeEventListener(
				'click',
				this.setAccountDialogPositionHandler,
				{ capture: true }
			)
		}
		this.removeEventListener(
			'preventHeaderReveal',
			this.hideHeaderOnScrollUp
		)
		window.removeEventListener('scroll', this.onScrollHandler)
		window.clearTimeout(this.topTransitionTimeout)
	}

	setAccountDialogPosition() {
		if (!this.accountComponent) return

		this.accountComponent.style.setProperty(
			'--shopify-account-dialog-position-top',
			`${Math.max(0, this.getBoundingClientRect().bottom)}px`
		)
	}

	createObserver() {
		this.headerBounds = this.header.getBoundingClientRect()

		let observer = new IntersectionObserver((entries, observer) => {
			this.headerBounds = entries[0].intersectionRect
			observer.disconnect()
		})

		observer.observe(this.header)
	}

	onScroll() {
		const scrollTop =
			window.pageYOffset || document.documentElement.scrollTop

		if (this.predictiveSearch && this.predictiveSearch.isOpen) return
		if (
			this.headerIsAlwaysSticky &&
			this.headerBounds instanceof DOMRectReadOnly
		) {
			this.setBackdropState(
				scrollTop > this.headerBounds.bottom ? 'visible' : 'top'
			)
		}

		if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop > this.currentScrollTop &&
			scrollTop > this.headerBounds.bottom
		) {
			this.scrollDirectionAnchor = scrollTop
			this.cancelTopTransition()
			this.header.classList.add('scrolled-past-header')
			if (!this.preventHide) {
				requestAnimationFrame(() => {
					if (!this.preventHide) this.hide()
				})
			}
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop < this.currentScrollTop &&
			scrollTop > this.headerBounds.bottom
		) {
			this.cancelTopTransition()
			this.header.classList.add('scrolled-past-header')
			if (
				!this.preventReveal &&
				this.scrollDirectionAnchor - scrollTop >= 10
			) {
				requestAnimationFrame(() => {
					if (!this.preventReveal) this.reveal()
				})
			} else {
				window.clearTimeout(this.isScrolling)

				this.isScrolling = setTimeout(() => {
					this.preventReveal = false
				}, 66)

				if (!this.preventHide) {
					requestAnimationFrame(() => {
						if (!this.preventHide) this.hide()
					})
				}
			}
		} else if (
			this.headerBounds instanceof DOMRectReadOnly &&
			scrollTop <= Math.ceil(this.headerBounds.top)
		) {
			this.scrollDirectionAnchor = scrollTop
			requestAnimationFrame(this.reset.bind(this))
		} else if (!(this.headerBounds instanceof DOMRectReadOnly)) {
			console.warn(
				'onScroll used in sticky-header but headerBounds is not initialized'
			)
			console.log('headerBounds', this.headerBounds)
		}

		this.currentScrollTop = scrollTop
	}

	hide() {
		if (this.headerIsAlwaysSticky) return
		const shouldAnimate =
			this.header.classList.contains('shopify-section-header-sticky') &&
			!this.header.classList.contains('shopify-section-header-hidden')
		this.cancelTopTransition()
		this.header.classList.add(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky'
		)
		this.header.classList.toggle('animate', shouldAnimate)
		this.setBackdropState('hidden')
		this.closeMenuDisclosure()
		this.closeSearchModal()
	}

	reveal() {
		if (this.headerIsAlwaysSticky) return
		this.cancelTopTransition()
		this.header.classList.add('shopify-section-header-sticky', 'animate')
		this.header.classList.remove('shopify-section-header-hidden')
		this.setBackdropState('visible')
	}

	reset() {
		if (this.headerIsAlwaysSticky) return
		if (this.header.classList.contains('header-returning-to-top')) return

		const wasScrolled = this.header.classList.contains(
			'scrolled-past-header'
		)
		this.header.classList.remove(
			'shopify-section-header-hidden',
			'shopify-section-header-sticky',
			'animate'
		)
		this.setBackdropState('top')

		if (!wasScrolled) return

		this.header.classList.add('header-returning-to-top')
		this.header.classList.remove('scrolled-past-header')

		const finishTransition = () => {
			this.header.classList.remove('header-returning-to-top')
			this.topTransitionTimeout = undefined
		}
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			finishTransition()
			return
		}

		this.topTransitionTimeout = window.setTimeout(finishTransition, 220)
	}

	cancelTopTransition() {
		window.clearTimeout(this.topTransitionTimeout)
		this.topTransitionTimeout = undefined
		this.header.classList.remove('header-returning-to-top')
	}

	setBackdropState(state: 'top' | 'hidden' | 'visible') {
		if (!this.backdrop) return
		this.backdrop.toggleAttribute(
			'data-visible',
			this.usesTransparentBackdrop && state === 'visible'
		)
		this.backdrop.toggleAttribute(
			'data-hidden',
			this.usesTransparentBackdrop && state === 'hidden'
		)
	}

	closeMenuDisclosure() {
		this.disclosures =
			this.disclosures ||
			q.ol<HeaderMenu>('header-menu', this.header)
		this.disclosures?.forEach((disclosure) => {
			disclosure.close()
		})
	}

	closeSearchModal() {
		this.searchModal =
			this.searchModal ??
			q.os<DetailsModal>('details-modal', this.header)
		this.searchModal?.close(false)
	}
}
